import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import {
  ArrowLeft, ArrowRight, Check, Sparkles, Loader2, Lock, AlertTriangle,
  Bold, Italic, Heading2, Heading3, List, ListOrdered, ChevronLeft, ChevronRight, X,
} from "lucide-react";
import { ImageField } from "./ImageField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("nyla_admin_token")}` });

const inputCls =
  "w-full bg-[#F5F5F7] border border-[#D2D2D7] rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#2C3E35]";
const outputCls =
  "w-full bg-[#F5F5F7] border border-[#D2D2D7] rounded-2xl px-4 py-3 text-[13px] leading-relaxed font-mono focus:outline-none focus:border-[#2C3E35] resize-y";

export const STAGES = [
  { key: "keyword_research", label: "Keyword Research", kind: "ai" },
  { key: "search_intent", label: "Search Intent", kind: "ai" },
  { key: "ai_research", label: "Research", kind: "ai" },
  { key: "ai_draft", label: "First Draft", kind: "ai" },
  { key: "expert_inputs", label: "Nyla Expert Inputs", kind: "manual" },
  { key: "factual_verification", label: "Factual Verification", kind: "manual" },
  { key: "original_info", label: "Original Nyla Information", kind: "manual" },
  { key: "human_editing", label: "Human Editing", kind: "manual" },
  { key: "seo_optimization", label: "SEO Optimization", kind: "ai" },
  { key: "json_ld", label: "JSON-LD", kind: "auto" },
  { key: "publish", label: "Publish", kind: "publish" },
];

const emptyWf = () => ({
  topic: "",
  keyword: "",
  stages: Object.fromEntries(
    STAGES.filter((s) => s.kind !== "publish").map((s) => [
      s.key,
      { output: "", done: s.key === "json_ld" },
    ])
  ),
});

const MARKER_SOURCE = "\\[(VERIFY|NYLA[ _]INPUT)\\]";

const findMarkers = (editor) => {
  const found = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      const re = new RegExp(MARKER_SOURCE, "gi");
      let m;
      while ((m = re.exec(node.text))) {
        found.push({ from: pos + m.index, to: pos + m.index + m[0].length, label: m[0] });
      }
    }
  });
  return found;
};

const GenerateBtn = ({ onClick, busy, label = "Generate with AI" }) => (
  <button
    data-testid="wizard-generate-button"
    onClick={onClick}
    disabled={!!busy}
    className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1D1D1F] text-white text-xs font-medium hover:bg-black disabled:opacity-60 transition-colors duration-200"
  >
    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
    {busy ? "Generating…" : label}
  </button>
);

const ToolBtn = ({ onClick, active, children, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className={`p-2 rounded-lg transition-colors duration-150 ${active ? "bg-[#1D1D1F] text-white" : "text-[#1D1D1F]/70 hover:bg-black/5"}`}
  >
    {children}
  </button>
);

export const WorkflowWizard = ({ post, onDone, onUnauthorized }) => {
  const [postId, setPostId] = useState(post?.id || null);
  const [step, setStep] = useState(0);
  const [wf, setWf] = useState(post?.workflow?.stages ? post.workflow : emptyWf());
  const [form, setForm] = useState({
    title: post?.title || "",
    excerpt: post?.excerpt || "",
    category: post?.category || "Journal",
    image: post?.image || "",
    read_time: post?.read_time || "4 min read",
  });
  const [contentHtml, setContentHtml] = useState(post?.content_html || "");
  const [generating, setGenerating] = useState("");
  const [saving, setSaving] = useState(false);
  const [markerSel, setMarkerSel] = useState(null);
  const [markerInput, setMarkerInput] = useState("");
  const editorWrapRef = useRef(null);

  const stage = STAGES[step];
  const st = (key) => wf.stages[key] || { output: "", done: false };
  const setStage = (key, patch) =>
    setWf((prev) => ({ ...prev, stages: { ...prev.stages, [key]: { ...prev.stages[key], ...patch } } }));
  const requiredDone = STAGES.filter((s) => s.kind !== "publish" && s.kind !== "auto")
    .every((s) => st(s.key).done);

  const showMarkerPanel = (e, marker) => {
    const wrap = editorWrapRef.current;
    if (!wrap) return;
    const coords = e.view.coordsAtPos(marker.from);
    const rect = wrap.getBoundingClientRect();
    setMarkerSel({
      ...marker,
      top: coords.bottom - rect.top + 8,
      left: Math.max(0, Math.min(coords.left - rect.left, rect.width - 340)),
    });
    setMarkerInput("");
  };

  const handleMarkerSelection = (e) => {
    const sel = e.state.selection;
    const markers = findMarkers(e);
    const exact = markers.find((m) => m.from === sel.from && m.to === sel.to);
    if (exact) {
      showMarkerPanel(e, exact);
      return;
    }
    const inside = markers.find((m) => sel.from > m.from && sel.to < m.to);
    if (inside) {
      e.chain().setTextSelection({ from: inside.from, to: inside.to }).run();
      return;
    }
    setMarkerSel(null);
  };

  const editor = useEditor({
    extensions: [StarterKit.configure({ link: { openOnClick: false } }), Image],
    content: contentHtml || "<p></p>",
    onUpdate: ({ editor: e }) => setContentHtml(e.getHTML()),
    onSelectionUpdate: ({ editor: e }) => handleMarkerSelection(e),
    editorProps: {
      attributes: {
        class: "prose-editor min-h-[280px] focus:outline-none px-5 py-4 text-[15px] leading-relaxed",
        "data-testid": "wizard-content-editor",
      },
    },
  });

  const jumpMarker = (dir) => {
    if (!editor) return;
    const markers = findMarkers(editor);
    if (!markers.length) return;
    const pos = editor.state.selection.from;
    let target;
    if (dir > 0) {
      target = markers.find((m) => m.from > pos) || markers[0];
    } else {
      const before = markers.filter((m) => m.to < pos);
      target = before[before.length - 1] || markers[markers.length - 1];
    }
    editor.chain().focus().setTextSelection({ from: target.from, to: target.to }).scrollIntoView().run();
  };

  const markerStillValid = () =>
    markerSel && editor && editor.state.doc.textBetween(markerSel.from, markerSel.to, " ") === markerSel.label;

  const resolveMarker = (replacement) => {
    if (!markerStillValid()) {
      setMarkerSel(null);
      toast.error("The article changed — click the marker again");
      return;
    }
    editor.chain().focus().insertContentAt({ from: markerSel.from, to: markerSel.to }, replacement).run();
    setMarkerSel(null);
    setMarkerInput("");
    toast.success(replacement ? "Information inserted" : "Marker removed");
  };

  useEffect(() => {
    if (stage.key === "human_editing" && editor && contentHtml && editor.getHTML() !== contentHtml) {
      editor.commands.setContent(contentHtml);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, editor]);

  const streamGenerate = async (stageKey, context, apply) => {
    setGenerating(stageKey);
    try {
      const res = await fetch(`${API}/admin/ai/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ stage: stageKey, context }),
      });
      if (res.status === 401) return onUnauthorized();
      if (!res.ok) throw new Error("bad status");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        apply(acc);
      }
    } catch {
      toast.error("Generation failed — please try again");
    } finally {
      setGenerating("");
    }
  };

  const completeAndNext = async (validate) => {
    if (validate && !validate()) return;
    setStage(stage.key, { done: true });
    const nextWf = { ...wf, stages: { ...wf.stages, [stage.key]: { ...st(stage.key), done: true } } };
    setWf(nextWf);
    try {
      await persistWith(nextWf);
      setStep((s) => Math.min(s + 1, STAGES.length - 1));
    } catch {}
  };

  const persistWith = async (workflow, extra = {}) => {
    const payload = {
      ...form,
      title: form.title.trim() || workflow.topic.trim() || "Untitled draft",
      content_html: contentHtml,
      status: "draft",
      workflow,
      ...extra,
    };
    setSaving(true);
    try {
      if (postId) {
        const { data } = await axios.put(`${API}/admin/blog/${postId}`, payload, { headers: auth() });
        return data;
      }
      const { data } = await axios.post(`${API}/admin/blog`, payload, { headers: auth() });
      setPostId(data.id);
      return data;
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else toast.error(e.response?.data?.detail || "Save failed");
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const markerCounts = () => {
    const verify = (contentHtml.match(/\[VERIFY\]/gi) || []).length;
    const nyla = (contentHtml.match(/\[NYLA INPUT\]/gi) || []).length;
    return { verify, nyla, total: verify + nyla };
  };

  const MarkerWarning = () => {
    const m = markerCounts();
    if (!m.total) return null;
    return (
      <div
        data-testid="marker-warning"
        className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3"
      >
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-[13px] leading-relaxed text-amber-800">
          <span className="font-semibold">
            {m.total} unresolved marker{m.total > 1 ? "s" : ""} in the article:
          </span>{" "}
          {m.verify > 0 && <>{m.verify} × [VERIFY]</>}
          {m.verify > 0 && m.nyla > 0 && " and "}
          {m.nyla > 0 && <>{m.nyla} × [NYLA INPUT]</>}
          . Resolve each one — verify the fact or add the Nyla-specific detail, then remove the
          marker text — before publishing.
        </p>
      </div>
    );
  };

  const publish = async () => {
    const m = markerCounts();
    if (m.total > 0 && !window.confirm(
      `The article still contains ${m.total} unresolved marker(s) ([VERIFY]/[NYLA INPUT]). Publish anyway?`
    )) return;
    try {
      await persistWith(wf, { status: "published" });
      toast.success("Published — live on the site");
      onDone(true);
    } catch {}
  };

  const ctx = () => ({
    topic: wf.topic,
    keyword: wf.keyword,
    intent: st("search_intent").output,
    research: st("ai_research").output,
    draft: contentHtml,
    title: form.title,
    excerpt: form.excerpt,
  });

  const stageDescriptions = {
    keyword_research: "Enter the topic you want to write about. AI will research the best keywords to target in the premium water & wellness niche.",
    search_intent: "AI analyses what searchers actually want when they type your primary keyword — so the article satisfies them fully.",
    ai_research: "AI builds a research brief: outline, key facts (flagged [VERIFY] for fact-checking) and spots for Nyla-specific input (flagged [NYLA INPUT]).",
    ai_draft: "AI writes the full first draft following the research brief. Review it, then apply it to the article.",
    expert_inputs: "Record insights from the Nyla team — process details, expert perspective, quotes. Weave them into the draft in the editing stage.",
    factual_verification: "Go through every [VERIFY] marker in the draft and confirm each fact against a reliable source.",
    original_info: "Add information only Nyla can provide — original data, numbers, photos, first-hand details. This is what makes the article rank.",
    human_editing: "Edit the article by hand: weave in expert inputs, resolve all markers, polish the voice, set the cover image.",
    seo_optimization: "AI reviews the near-final article and recommends title, meta description, slug, and keyword placement improvements.",
    json_ld: "Done automatically. Every article emits BlogPosting structured data (headline, author, publisher, word count, breadcrumbs) the moment it renders — old and future posts alike.",
    publish: "Final check — publishing is unlocked only when every stage is complete.",
  };

  const renderStage = () => {
    const s = st(stage.key);
    switch (stage.key) {
      case "keyword_research":
        return (
          <>
            <label className="text-xs font-semibold text-[#86868B] uppercase tracking-wide">Blog topic *</label>
            <input data-testid="wizard-topic-input" className={inputCls} placeholder="e.g. Benefits of alkaline water for athletes"
              value={wf.topic} onChange={(e) => setWf({ ...wf, topic: e.target.value })} />
            <GenerateBtn busy={generating} onClick={() => {
              if (!wf.topic.trim()) return toast.error("Enter a topic first");
              streamGenerate("keyword_research", ctx(), (t) => setStage("keyword_research", { output: t }));
            }} />
            {s.output && (
              <textarea data-testid="wizard-stage-output" rows={14} className={outputCls} value={s.output}
                onChange={(e) => setStage("keyword_research", { output: e.target.value })} />
            )}
            <label className="text-xs font-semibold text-[#86868B] uppercase tracking-wide">Chosen primary keyword *</label>
            <input data-testid="wizard-keyword-input" className={inputCls} placeholder="Pick the primary keyword from the research above"
              value={wf.keyword} onChange={(e) => setWf({ ...wf, keyword: e.target.value })} />
          </>
        );
      case "search_intent":
      case "ai_research":
      case "seo_optimization":
        return (
          <>
            <p className="text-xs text-[#86868B]">
              Primary keyword: <span className="font-semibold text-[#1D1D1F]">{wf.keyword || "—"}</span>
            </p>
            <GenerateBtn busy={generating} onClick={() =>
              streamGenerate(stage.key, ctx(), (t) => setStage(stage.key, { output: t }))
            } />
            <textarea data-testid="wizard-stage-output" rows={16} className={outputCls}
              placeholder="Generate with AI — or paste your own notes here"
              value={s.output}
              onChange={(e) => setStage(stage.key, { output: e.target.value })} />
          </>
        );
      case "ai_draft":
        return (
          <>
            <GenerateBtn busy={generating} label="Generate first draft" onClick={() =>
              streamGenerate("ai_draft", ctx(), (t) => setStage("ai_draft", { output: t }))
            } />
            {s.output && (
              <>
                <textarea data-testid="wizard-stage-output" rows={18} className={outputCls} value={s.output}
                  onChange={(e) => setStage("ai_draft", { output: e.target.value })} />
                <button
                  data-testid="wizard-apply-draft"
                  onClick={() => { setContentHtml(s.output); toast.success("Draft applied to the article"); }}
                  className="px-5 py-2.5 rounded-full bg-[#2C3E35] text-white text-xs font-medium hover:bg-[#223129] transition-colors duration-200"
                >
                  Use as article content
                </button>
              </>
            )}
            {contentHtml && <p className="text-xs text-[#2C3E35] font-medium">✓ Article content is set ({contentHtml.replace(/<[^>]+>/g, " ").trim().split(/\s+/).length} words)</p>}
          </>
        );
      case "expert_inputs":
      case "original_info":
        return (
          <textarea data-testid="wizard-stage-output" rows={10} className={outputCls}
            placeholder={stage.key === "expert_inputs"
              ? "Notes from the Nyla team — process details, expert quotes, perspective…"
              : "Original Nyla data, numbers, first-hand details added to the article…"}
            value={s.output} onChange={(e) => setStage(stage.key, { output: e.target.value })} />
        );
      case "factual_verification":
        return (
          <>
            <textarea data-testid="wizard-stage-output" rows={6} className={outputCls}
              placeholder="Verification notes — which sources were used for each [VERIFY] fact (optional)"
              value={s.output} onChange={(e) => setStage("factual_verification", { output: e.target.value })} />
            <label className="flex items-center gap-3 text-sm text-[#1D1D1F] cursor-pointer">
              <input
                data-testid="wizard-verify-checkbox"
                type="checkbox"
                checked={!!s.confirmed}
                onChange={(e) => setStage("factual_verification", { confirmed: e.target.checked })}
                className="w-4 h-4 accent-[#2C3E35]"
              />
              I confirm every fact in the draft has been verified against a reliable source.
            </label>
          </>
        );
      case "human_editing":
        return (
          <>
            <MarkerWarning />
            <input data-testid="wizard-title-input" className={`${inputCls} text-lg font-semibold`} placeholder="Post title *"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <textarea data-testid="wizard-excerpt-input" rows={2} className={`${inputCls} resize-none`} placeholder="Short excerpt (shown on the card & as meta description)"
              value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
            <div className="grid sm:grid-cols-2 gap-4">
              <input className={inputCls} placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <input className={inputCls} placeholder="Read time" value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} />
            </div>
            <div>
              <p className="text-xs font-semibold text-[#86868B] uppercase tracking-wide mb-2">Cover Image (1600×900, 16:9)</p>
              <ImageField value={form.image} onChange={(v) => setForm({ ...form, image: v })} testId="wizard-image-input" aiPrompt={() => `Blog cover image for an article titled "${form.title || wf.topic}". ${form.excerpt || wf.keyword || ""}`} />
            </div>
            <div ref={editorWrapRef} className="relative">
              <div className="border border-[#D2D2D7] rounded-2xl overflow-hidden">
                <div className="flex items-center gap-1 px-3 py-2 border-b border-[#D2D2D7]/60 bg-[#F5F5F7] flex-wrap">
                  <ToolBtn label="Bold" active={editor?.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" /></ToolBtn>
                  <ToolBtn label="Italic" active={editor?.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" /></ToolBtn>
                  <ToolBtn label="Heading 2" active={editor?.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" /></ToolBtn>
                  <ToolBtn label="Heading 3" active={editor?.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></ToolBtn>
                  <ToolBtn label="Bullet list" active={editor?.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" /></ToolBtn>
                  <ToolBtn label="Numbered list" active={editor?.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></ToolBtn>
                  {markerCounts().total > 0 && (
                    <div className="ml-auto flex items-center gap-1.5 bg-amber-100/80 border border-amber-200 rounded-full pl-3 pr-1 py-1">
                      <span className="text-[11px] font-semibold text-amber-800">
                        {markerCounts().total} marker{markerCounts().total > 1 ? "s" : ""}
                      </span>
                      <button
                        type="button"
                        data-testid="marker-prev"
                        onClick={() => jumpMarker(-1)}
                        aria-label="Previous marker"
                        className="p-1 rounded-full text-amber-800 hover:bg-amber-200/80 transition-colors duration-150"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        data-testid="marker-next"
                        onClick={() => jumpMarker(1)}
                        aria-label="Next marker"
                        className="p-1 rounded-full text-amber-800 hover:bg-amber-200/80 transition-colors duration-150"
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <EditorContent editor={editor} />
              </div>
              {markerSel && (
                <div
                  data-testid="marker-action-panel"
                  className="absolute z-30 w-[340px] bg-white border border-[#D2D2D7] rounded-2xl shadow-xl p-4"
                  style={{ top: markerSel.top, left: markerSel.left }}
                >
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="text-[10px] tracking-[0.12em] uppercase font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                      {markerSel.label}
                    </span>
                    <button
                      type="button"
                      data-testid="marker-panel-close"
                      onClick={() => setMarkerSel(null)}
                      aria-label="Close"
                      className="p-1 rounded-full text-[#86868B] hover:bg-black/5"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-[11px] text-[#86868B] leading-relaxed mb-2.5">
                    {/verify/i.test(markerSel.label)
                      ? "Replace this marker with the verified fact, or remove it once checked."
                      : "Replace this marker with Nyla-specific information, or remove it."}
                  </p>
                  <textarea
                    data-testid="marker-replace-input"
                    rows={2}
                    autoFocus
                    value={markerInput}
                    onChange={(ev) => setMarkerInput(ev.target.value)}
                    placeholder="Type the information to insert here…"
                    className="w-full bg-[#F5F5F7] border border-[#D2D2D7] rounded-xl px-3 py-2 text-[13px] focus:outline-none focus:border-[#2C3E35] resize-none mb-2.5"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="marker-insert-button"
                      onClick={() => {
                        if (!markerInput.trim()) return toast.error("Type the information first");
                        resolveMarker(markerInput.trim());
                      }}
                      className="flex-1 px-3 py-2 rounded-full bg-[#2C3E35] text-white text-xs font-medium hover:bg-[#223129] transition-colors duration-200"
                    >
                      Insert information
                    </button>
                    <button
                      type="button"
                      data-testid="marker-remove-button"
                      onClick={() => resolveMarker("")}
                      className="px-3 py-2 rounded-full border border-[#D2D2D7] text-xs font-medium text-[#1D1D1F]/70 hover:bg-black/5 transition-colors duration-200"
                    >
                      Remove marker
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        );
      case "json_ld":
        return (
          <div className="bg-[#2C3E35]/5 border border-[#2C3E35]/20 rounded-2xl p-5 text-sm text-[#1D1D1F] leading-relaxed">
            <p className="font-semibold text-[#2C3E35] mb-1.5">✓ Nothing to do here</p>
            Structured data (BlogPosting schema with headline, author, publisher, word count, read
            time and breadcrumbs) is generated automatically for this article the moment it is
            rendered — Google will see it without any manual work.
          </div>
        );
      case "publish":
        return (
          <>
            <MarkerWarning />
            <div className="space-y-2">
              {STAGES.filter((x) => x.kind !== "publish").map((x) => (
                <div key={x.key} className="flex items-center gap-2.5 text-sm">
                  {st(x.key).done
                    ? <Check className="w-4 h-4 text-[#2C3E35]" />
                    : <Lock className="w-4 h-4 text-amber-500" />}
                  <span className={st(x.key).done ? "text-[#1D1D1F]" : "text-[#86868B]"}>{x.label}</span>
                </div>
              ))}
            </div>
            <button
              data-testid="wizard-publish-button"
              onClick={publish}
              disabled={!requiredDone || saving}
              className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#2C3E35] text-white text-sm font-medium hover:bg-[#223129] disabled:opacity-50 transition-colors duration-200"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {requiredDone ? "Publish — go live" : "Publishing locked until all stages are complete"}
            </button>
          </>
        );
      default:
        return null;
    }
  };

  const validators = {
    keyword_research: () => {
      if (!wf.topic.trim()) { toast.error("Enter a blog topic"); return false; }
      if (!st("keyword_research").output.trim()) { toast.error("Generate the keyword research first"); return false; }
      if (!wf.keyword.trim()) { toast.error("Choose a primary keyword"); return false; }
      return true;
    },
    search_intent: () => st("search_intent").output.trim() ? true : (toast.error("Generate the intent analysis first"), false),
    ai_research: () => st("ai_research").output.trim() ? true : (toast.error("Generate the research brief first"), false),
    ai_draft: () => contentHtml.trim() ? true : (toast.error("Generate a draft and apply it to the article"), false),
    expert_inputs: () => st("expert_inputs").output.trim() ? true : (toast.error("Add the Nyla expert inputs"), false),
    factual_verification: () => st("factual_verification").confirmed ? true : (toast.error("Tick the verification confirmation"), false),
    original_info: () => st("original_info").output.trim() ? true : (toast.error("Describe the original Nyla information added"), false),
    human_editing: () => {
      if (!form.title.trim()) { toast.error("Title is required"); return false; }
      if (!contentHtml.trim()) { toast.error("Article content is empty"); return false; }
      return true;
    },
    seo_optimization: () => st("seo_optimization").output.trim() ? true : (toast.error("Generate the SEO report first"), false),
    json_ld: () => true,
  };

  return (
    <div data-testid="workflow-wizard">
      <button onClick={() => onDone(false)} className="flex items-center gap-1.5 text-sm text-[#86868B] hover:text-[#1D1D1F] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to posts
      </button>

      <div className="grid lg:grid-cols-[240px_1fr] gap-6 items-start">
        <div className="bg-white rounded-3xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] lg:sticky lg:top-20">
          <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-4 px-2">Content Workflow</p>
          <div className="space-y-0.5">
            {STAGES.map((x, i) => {
              const done = x.kind === "publish" ? false : st(x.key).done;
              const active = i === step;
              return (
                <button
                  key={x.key}
                  data-testid={`wizard-step-${x.key}`}
                  onClick={() => setStep(i)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left text-[13px] transition-colors duration-150 ${
                    active ? "bg-[#1D1D1F] text-white" : "text-[#1D1D1F]/70 hover:bg-black/5"
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                    done ? "bg-[#2C3E35] text-white" : active ? "bg-white/20 text-white" : "bg-black/5 text-[#86868B]"
                  }`}>
                    {done ? <Check className="w-3 h-3" /> : i + 1}
                  </span>
                  {x.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-1.5">
            Step {step + 1} of {STAGES.length}
          </p>
          <h2 className="font-display text-2xl font-bold tracking-tight mb-2">{stage.label}</h2>
          <p className="text-sm text-[#86868B] leading-relaxed mb-6">{stageDescriptions[stage.key]}</p>

          <div className="space-y-4">{renderStage()}</div>

          {stage.kind !== "publish" && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#D2D2D7]/50">
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="flex items-center gap-1.5 text-sm text-[#86868B] hover:text-[#1D1D1F] disabled:opacity-40"
              >
                <ArrowLeft className="w-4 h-4" /> Previous
              </button>
              <button
                data-testid="wizard-complete-step"
                onClick={() => completeAndNext(validators[stage.key])}
                disabled={saving || !!generating}
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#1D1D1F] text-white text-sm font-medium hover:bg-black disabled:opacity-60 transition-colors duration-200"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Mark complete & continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
